import { useEffect, useState } from 'react';
import { useUIStore, useProjectStore } from '@/stores';
import { Modal, Button, Loader } from '@/components/ui';
import { Folder, Calendar, Tag, FileText, Trash2, AlertTriangle, X } from 'lucide-react';
import type { Version } from '@/types';
import { versionsApi } from '@/api/client';

interface ProjectWithVersion {
  id: string;
  name: string;
  description: string;
  supplier: string;
  status: string;
  created_at: string;
  latestVersion: Version | null;
}

export function ProjectListModal() {
  const { isProjectListModalOpen, closeProjectListModal } = useUIStore();
  const { projects, setCurrentProject, deleteProject } = useProjectStore();
  const [isLoading, setIsLoading] = useState(false);
  const [projectsWithVersions, setProjectsWithVersions] = useState<ProjectWithVersion[]>([]);
  const [hasLoaded, setHasLoaded] = useState(false);
  
  // 删除相关状态
  const [projectToDelete, setProjectToDelete] = useState<ProjectWithVersion | null>(null);
  const [isDeleting, setIsDeleting] = useState(false);

  useEffect(() => {
    if (isProjectListModalOpen && !hasLoaded) {
      loadProjectsWithVersions();
    }
  }, [isProjectListModalOpen, hasLoaded]);

  // 当弹窗关闭时重置加载状态
  useEffect(() => {
    if (!isProjectListModalOpen) {
      setHasLoaded(false);
      setProjectsWithVersions([]);
      setProjectToDelete(null);
    }
  }, [isProjectListModalOpen]);

  const loadProjectsWithVersions = async () => {
    setIsLoading(true);
    try {
      // 使用已有的 projects，不再调用 loadProjects 避免触发全局 loading
      const currentProjects = useProjectStore.getState().projects;
      
      // 如果 projects 为空，静默加载（不触发 isLoading 状态）
      let projectsToUse = currentProjects;
      if (projectsToUse.length === 0) {
        try {
          const { projectsApi } = await import('@/api/client');
          projectsToUse = await projectsApi.getAll();
        } catch (e) {
          console.error('Failed to load projects:', e);
        }
      }
      
      const projectsWithVersionsData: ProjectWithVersion[] = await Promise.all(
        projectsToUse.map(async (project) => {
          try {
            const versions = await versionsApi.getAll(project.id);
            const latestVersion = versions.length > 0 
              ? versions.reduce((latest, current) => 
                  new Date(current.created_at) > new Date(latest.created_at) ? current : latest
                )
              : null;
            
            return {
              id: project.id,
              name: project.name,
              description: project.description,
              supplier: project.supplier,
              status: project.status,
              created_at: project.created_at,
              latestVersion,
            };
          } catch (error) {
            console.error('Error loading versions for project:', project.id, error);
            return {
              id: project.id,
              name: project.name,
              description: project.description,
              supplier: project.supplier,
              status: project.status,
              created_at: project.created_at,
              latestVersion: null,
            };
          }
        })
      );
      
      setProjectsWithVersions(projectsWithVersionsData);
      setHasLoaded(true);
    } catch (error) {
      console.error('Error loading projects with versions:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const handleProjectClick = async (project: ProjectWithVersion) => {
    try {
      // 在切换项目前设置 isProjectSwitching 为 true
      useProjectStore.setState({ isProjectSwitching: true });

      // 直接从 store 获取最新的 projects
      const { projects: latestProjects } = useProjectStore.getState();
      const targetProject = latestProjects.find(p => p.id === project.id);
      if (targetProject) {
        // 使用正确的字段名：task_list
        if (project.latestVersion?.task_list) {
          setCurrentProject({
            ...targetProject,
            taskList: project.latestVersion.task_list,
          });
        } else {
          setCurrentProject(targetProject);
        }
        closeProjectListModal();
      }
    } catch (error) {
      console.error('Error loading project:', error);
      // 确保出错时重置状态
      useProjectStore.setState({ isProjectSwitching: false });
    }
  };

  const handleDeleteClick = (e: React.MouseEvent, project: ProjectWithVersion) => {
    e.stopPropagation(); // 阻止冒泡，避免触发卡片点击
    setProjectToDelete(project);
  };

  const handleConfirmDelete = async () => {
    if (!projectToDelete) return;
    
    setIsDeleting(true);
    try {
      await deleteProject(projectToDelete.id);
      
      // 从本地列表中移除
      setProjectsWithVersions(prev => prev.filter(p => p.id !== projectToDelete.id));
      
      // 关闭确认弹窗
      setProjectToDelete(null);
    } catch (error) {
      console.error('Delete project error:', error);
    } finally {
      setIsDeleting(false);
    }
  };

  const handleCancelDelete = () => {
    setProjectToDelete(null);
  };

  const formatDate = (dateStr: string) => {
    const date = new Date(dateStr);
    return date.toLocaleDateString('en-US', {
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  const getStatusLabel = (status: string) => {
    const statusMap: Record<string, string> = {
      active: 'Active',
      completed: 'Completed',
      archived: 'Archived',
    };
    return statusMap[status] || status;
  };

  const getStatusColor = (status: string) => {
    const colorMap: Record<string, string> = {
      active: 'bg-green-100 text-green-700',
      completed: 'bg-blue-100 text-blue-700',
      archived: 'bg-gray-100 text-gray-700',
    };
    return colorMap[status] || 'bg-gray-100 text-gray-700';
  };

  return (
    <>
      <Modal
        isOpen={isProjectListModalOpen}
        onClose={closeProjectListModal}
        title="Project List"
        size="xl"
      >
        <div className="space-y-4">
          {isLoading ? (
            <div className="flex items-center justify-center py-12">
              <div className="w-20 h-20 flex items-center justify-center">
                <Loader size="md" />
              </div>
            </div>
          ) : projectsWithVersions.length === 0 ? (
            <div className="text-center py-12 text-gray-400">
              <Folder className="w-12 h-12 mx-auto mb-3 opacity-50" />
              <p>No projects found</p>
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 max-h-[500px] overflow-y-auto">
              {projectsWithVersions.map((project) => (
                <div
                  key={project.id}
                  onClick={() => handleProjectClick(project)}
                  className="bg-white border border-gray-200 rounded-xl p-5 hover:shadow-lg hover:border-blue-300 transition-all cursor-pointer group relative"
                >
                  {/* 删除按钮 */}
                  <button
                    onClick={(e) => handleDeleteClick(e, project)}
                    className="absolute bottom-3 right-3 p-2 rounded-lg text-gray-400 hover:text-red-600 hover:bg-red-50 transition-colors opacity-0 group-hover:opacity-100"
                    title="Delete project"
                  >
                    <Trash2 className="w-5 h-5" />
                  </button>

                  <div className="flex items-start justify-between mb-3">
                    <div className="flex items-center space-x-2">
                      <Folder className="w-5 h-5 text-blue-600" />
                      <h3 className="font-semibold text-gray-800 group-hover:text-blue-600 transition-colors">
                        {project.name}
                      </h3>
                    </div>
                    <span className={`px-2 py-1 rounded-full text-xs font-medium ${getStatusColor(project.status)}`}>
                      {getStatusLabel(project.status)}
                    </span>
                  </div>

                  {project.description && (
                    <p className="text-sm text-gray-600 mb-3 line-clamp-2">
                      {project.description}
                    </p>
                  )}

                  <div className="space-y-2 text-sm pr-10">
                    <div className="flex items-center text-gray-600">
                      <Tag className="w-4 h-4 mr-2 text-gray-400" />
                      <span>Supplier: {project.supplier}</span>
                    </div>

                    {project.latestVersion ? (
                      <>
                        <div className="flex items-center text-gray-600">
                          <FileText className="w-4 h-4 mr-2 text-gray-400" />
                          <span>Latest Version: {project.latestVersion.version}</span>
                        </div>
                        <div className="flex items-center text-gray-600">
                          <Calendar className="w-4 h-4 mr-2 text-gray-400" />
                          <span>Created: {formatDate(project.latestVersion.created_at)}</span>
                        </div>
                      </>
                    ) : (
                      <div className="text-gray-400 text-xs italic">
                        No versions available
                      </div>
                    )}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </Modal>

      {/* 删除确认弹窗 */}
      <Modal
        isOpen={!!projectToDelete}
        onClose={handleCancelDelete}
        title="Confirm Delete"
        size="sm"
      >
        <div className="space-y-4">
          <div className="flex items-start space-x-3">
            <div className="p-2 bg-red-100 rounded-full">
              <AlertTriangle className="w-6 h-6 text-red-600" />
            </div>
            <div>
              <h3 className="font-semibold text-gray-800 mb-1">
                Delete Project
              </h3>
              <p className="text-sm text-gray-600">
                Are you sure you want to delete <strong>{projectToDelete?.name}</strong>?
              </p>
              <p className="text-xs text-gray-500 mt-2">
                This will delete the project and all its versions permanently. This action cannot be undone.
              </p>
            </div>
          </div>

          <div className="flex justify-end space-x-3 pt-4 border-t border-gray-200">
            <Button
              variant="ghost"
              onClick={handleCancelDelete}
              disabled={isDeleting}
            >
              Cancel
            </Button>
            <Button
              variant="danger"
              onClick={handleConfirmDelete}
              isLoading={isDeleting}
              className="bg-red-600 hover:bg-red-700 text-white"
            >
              Delete
            </Button>
          </div>
        </div>
      </Modal>
    </>
  );
}
